import MoreVert from "@mui/icons-material/MoreVert";
import OpenInNew from "@mui/icons-material/OpenInNew";
import { Box, Link, Stack, Typography, useTheme, Menu, MenuItem, MenuList, ListItemIcon, ListItemText } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  CommunicationType,
  CommunicationStatus,
  CommunicationsQuery,
} from "generated-graphql/graphql";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";

import React, { useMemo, useState } from "react";
import { COMMUNICATIONS_QUERY } from "../../../MailRoom/Communications/api";

interface SupplementalAgencyCommDetailsProps {
  activityId: string;
}


type CommunicationItem = NonNullable<
  CommunicationsQuery["communications"]
>["items"][0];

const ActionsMenuCell: React.FC<{ communication: CommunicationItem }> = ({ communication }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleViewCommunication = () => {
    window.open(
      `/staff/mail-room/status/${communication.id}`,
      "_blank",
      "noopener,noreferrer"
    );
    handleClose();
  };

  const handleViewJob = () => {
    window.open(
      `/staff/jobs/${communication.jobId}`,
      "_blank",
      "noopener,noreferrer"
    );
    handleClose();
  };

  return (
    <>
      <GridActionsCellItem
        icon={<MoreVert />}
        label="Actions"
        onClick={handleClick}
        aria-controls={open ? "actions-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
      />
      <Menu
        id="actions-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "actions-button",
        }}
      >
        <MenuList dense>
          <MenuItem onClick={handleViewCommunication}>
            <ListItemIcon>
              <OpenInNew fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Communication</ListItemText>
          </MenuItem>
          {communication.jobId && (
            <MenuItem onClick={handleViewJob}>
              <ListItemIcon>
                <OpenInNew fontSize="small" />
              </ListItemIcon>
              <ListItemText>View Job</ListItemText>
            </MenuItem>
          )}
        </MenuList>
      </Menu>
    </>
  );
};

export const SupplementalAgencyCommDetails: React.FC<
  SupplementalAgencyCommDetailsProps
> = ({ activityId }) => {
  const theme = useTheme();

  const getAgencyName = (communication: CommunicationItem): string => {
    if (communication.fireDepartment?.name) {
      return communication.fireDepartment.name;
    }
    if (communication.lepc?.name) {
      return communication.lepc.name;
    }
    return communication.recipientName || "Unknown";
  };

  const getAgencyType = (communication: CommunicationItem): string => {
    if (communication.fireDepartment) {
      return "🚒 Fire Department";
    }
    if (communication.lepc) {
      return "🚓 LEPC";
    }
    return "Custom";
  };

  const getCommunicationType = (communication: CommunicationItem): string => {
    if (communication.communicationType === CommunicationType.Email) {
      return "📧 Email";
    }
    if (
      communication.communicationType === CommunicationType.CertifiedMail ||
      communication.communicationType === CommunicationType.FirstClassMail
    ) {
      return "📬 Mail";
    }
    return communication.communicationType || "Unknown";
  };

  const getStatusColor = (status: CommunicationStatus): string => {
    switch (status) {
      case CommunicationStatus.Error:
        return theme.palette.error.main;
      case CommunicationStatus.Pending:
        return theme.palette.warning.main;
      case CommunicationStatus.SentToEmailClient:
      case CommunicationStatus.SentToMailClient:
        return theme.palette.success.main;
      case CommunicationStatus.Draft:
        return theme.palette.text.secondary;
      default:
        return theme.palette.text.primary;
    }
  };

  const getItems = (data: CommunicationsQuery) =>
    data?.communications?.items || [];
  const getCount = (data: CommunicationsQuery) =>
    data?.communications?.count || 0;

  const columns: OmnisearchGridColDef<CommunicationItem>[] = [
    {
      field: "agencyName",
      headerName: "Agency Name",
      width: 240,
      valueGetter: (_, row) => getAgencyName(row),
    },
    {
      field: "agencyType",
      headerName: "Agency Type",
      width: 140,
      valueGetter: (_, row) => getAgencyType(row),
    },
    {
      field: "communicationType",
      headerName: "Type",
      width: 70,
      valueGetter: (_, row) => getCommunicationType(row),
    },

    {
      field: "status",
      headerName: "Status",
      width: 180,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            color: getStatusColor(params.value),
            fontWeight: 500,
          }}
        >
          {prettyPrintEnumValue(params.value)}
        </Typography>
      ),
    },
    {
      field: "proofDocument",
      headerName: "Proof Document",
      width: 130,
      renderCell: (params) => {
        const hasProof = Boolean(params.row.proofDocument?.id);
        return (
          <Typography
            variant="body2"
            sx={{
              color: hasProof
                ? theme.palette.success.main
                : theme.palette.warning.main,
              fontWeight: 500,
            }}
          >
            {hasProof ? "✅ Received" : "⏳ Waiting"}
          </Typography>
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      width: 80,
      align: "right",
      resizable: false,
      renderCell: (params) => {
        return <ActionsMenuCell communication={params.row} />;
      },
    },
  ];

  return (
    <Box sx={{ paddingTop: theme.spacing(2) }}>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 500 }}>
        Supplemental Agency Communications
      </Typography>
      <Box my={3}>
        <OmnisearchDataGrid
          persistenceKey="supplemental-agency-communications"
          columns={columns}
          dataQuery={COMMUNICATIONS_QUERY}
          defaultSearch={`activityId:${activityId}`}
          getItems={getItems}
          getCount={getCount}
          withPadding={false}
          initialPageSize={25}
          sx={{
            "& .MuiDataGrid-row:hover": {
              backgroundColor: theme.palette.action.hover,
            },
          }}
          hideOmnisearch={true}
          hideFooter={false}
          showFilters={false}
          showManageColumns={false}
          initialState={{
            pinnedColumns: {
              right: ["actions"],
            },
          }}
        />
      </Box>
    </Box>
  );
};
